import { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Button, Typography, Tooltip } from '@material-ui/core';
import { makeStyles } from "@material-ui/core/styles";
import ErrorIcon from '@material-ui/icons/Error';

import Colors from '../../../config/colors';
import SchemaBuilder from './SchemaBuilder';
import DataBuilder from './DataBuilder';
import { getAIWidgetTransactionDetails, getCodeEditStatus, getElementOnFocus } from '../../../store/global/selectors';
import { generateWidgetTransactionWithAI, handleSnackBar, invokeGetService, invokePostService, setFocusElement, setIsDocumentClicked, setReRender, setAiRequestEntity, updateAiAssistant } from "../../../store/global/actions";
import { getDetailsFromUrl } from '../../../services/blueprint';
import { editView, resetView, setView } from "../../../store/views/actions";
import { getViews } from "../../../store/views/selectors";
import { formatFetchedCustomIcons, generateMongoId } from "../../../utils/commonUtils";
import BottomDrawer from "../bluePrintComponents/BottomDrawer/BottomDrawer";
import View from "../../tabs/View";
import ViewSection from "../../tabs/ViewSection";
import ViewUI from "../../tabs/ViewUI";
import ViewTree from "./ViewTree";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import EditableTitle from "../../atoms/inputElemets/EditableTitle";
import { widgetGroups } from "../../../config/common";
import { LearnLLMButton } from "../../atoms/buttons/LearnLLMButton";
import { Alert } from "@material-ui/lab";
import DataSourceMapping from "./DataSourceMapping";
import { validateSingleView } from "../../../utils/DashboardBuilder";
import { controlTypes, widthHeightSchema } from "../../../config/fields";
import AiGenerationNotifications from "../PromptModal/AiGenerationNotifications";
import { generateUniqueEventName } from "../../../utils/llmUtils";
import OwnershipStatusLabel from "../../atoms/labels/OwnershipStatusLabel";
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const useStyles = makeStyles({
    root: {
        paddingTop: 76
    },
    button: {
        borderRadius: "20px",
        padding: "8px 16px",
        minWidth: "80px",
        textTransform: "none",
        backgroundColor: "#fff",
        color: Colors.primary,
        border: `1px solid ${Colors.primary}`,
        "&:hover": {
            backgroundColor: "#536779",
            color: "#fff"
        },
        "&:focus": {
            outline: "1px"
        }
    },
    activeButton: {
        borderRadius: "20px",
        padding: "8px 16px",
        minWidth: "80px",
        textTransform: "none",
        backgroundColor: Colors.primary,
        color: "#fff",
        border: `1px solid ${Colors.primary}`,
        "&:hover": {
            backgroundColor: Colors.primary,
            color: "#fff",
        },
        "&:focus": {
            outline: "1px"
        }
    },
    builderContainer: {
        margin: '0px 10px 15px'
    },
    avatar: {
        width: '55px',
        height: '55px',
        padding: '0.8rem'
    },
    iconImage: {
        width: '100%'
    },
    viewName: {
        flexGrow: 1,
        fontFamily: "Roboto, Helvetica",
        fontSize: "16px",
        fontWeight: "bold",
        color: "#2c3b4a",
        marginLeft: "9px"
    },
    alertContainer: {
        margin: '10px 24px'
    },
    defaultViewAlertButton: {
        padding: '5px 20px',
        textTransform: 'capitalize'
    },
    defaultViewAlert: {
        width: '100%'
    },
    openAppBtn: {
      height: 26,
      minWidth: 0,
      padding: '0 8px',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1,
      textTransform: 'none',
      whiteSpace: 'nowrap',
      color: '#1a5fd6',
      borderColor: '#1a5fd6',
      borderRadius: 4,
      marginLeft: '0.5rem',
      '&:hover': {
        borderColor: '#1a5fd6',
        backgroundColor: 'rgba(26, 95, 214, 0.06)',
      },
    },
    openAppIcon: {
      fontSize: '14px !important', // override MUI startIcon default size
    },
    startIcon: {
      marginRight: 4,
      marginLeft: 0,
    },
});

const VIEW_DRAWERS = ["view", "section", "ui"]

const DashboardBuilder = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const selectedView = useSelector(getViews);
    const { pathname } = useLocation();
    const elementOnFocus = useSelector(getElementOnFocus);

    const [viewValidationWarnings, setVaildationWarnings] = useState([]);
    const [openTab, setOpenTab] = useState(0);
    const [drawerElement, setDrawerElement] = useState(null)
    const [isDrawerOpen, setDrawerOpen] = useState(false)
    const [viewSchema, setViewSchema] = useState(selectedView?.view_schema || []);
    const [sendTrainData, setSendTrainData] = useState(false)
    const [showDefaultViewAlert, setShowDefaultViewAlert] = useState(false);
    const [isAIGeneratingTransaction, setIsAIGeneratingTransaction] = useState(false);
    const [aiProgress, setAiProgress] = useState([]);
    const [notificationMessages, setNoficationMessage] = useState([]);
    const [messageList, setMessageList] = useState([]);
    const [deployBatches, setDeployBatches] = useState(null)

    const selectedIcon = selectedView?.icon ? formatFetchedCustomIcons(selectedView) : null;
    const codeEditStatus = useSelector(getCodeEditStatus);
    const aiWidgetTransactionDetails = useSelector(getAIWidgetTransactionDetails)

    const { entityId, blueprintId } = getDetailsFromUrl();
    

    const adjustFocusElement = (element) => {
        const type = element.type;

        if (type === 'view') {
            const viewName = element.action == "create" ? "Create View" : selectedView?.name;
            return { type: element.type, id: element.id, name: viewName, action: element.action }
        } else if (type === 'section') {
            return { type: element.type, id: element.id, name: element.name || "Create Section" }
        } else if (type === 'field') {
            return { type: element.type, id: element.id, name: element.name || "Create Field" }
        } else if (type === 'ui') {
            return { type: element.type, id: element.id, name: element.name || "Create Widget" }
        }
    }

    const disableDefaultViewAlert = () => {
        try {
            localStorage.setItem('DISABLE_DEFAULTVIEW_ALERT', 'true');
            if (showDefaultViewAlert) setShowDefaultViewAlert(false);
        } catch (error) {
            console.error("Failed to set DISABLE_DEFAULTVIEW_ALERT in localStorage:", error);
        }
    }

    const checkForDefaultViewAlert = () => {
        try {
            const isDisabled = localStorage.getItem('DISABLE_DEFAULTVIEW_ALERT');
            if (isDisabled === null) {
                setShowDefaultViewAlert(true);
            } else {
                setShowDefaultViewAlert(false);
            }
        } catch (error) {
            console.error("Error checking default view alert status:", error);
            setShowDefaultViewAlert(true); // fallback to showing alert in case of error
        }
    };


    const onSwitchTab = (index) => {
        if (codeEditStatus) {  // For alert to save when code is editing
            dispatch(setIsDocumentClicked());
            return;
        }
        setOpenTab(index);
    }

    useEffect(() => {
        dispatch(invokeGetService('getDeployBatches', {}))
          .then((response) => {
            setDeployBatches(response.batches || null)
          }).catch(error => {
            console.error(error)
          })
      },[blueprintId])

    useEffect(() => {

        const body = document.getElementsByTagName('body')?.[0];
        const mainContent = document.querySelector('#main-content-div')

        if (body) {
            body.style.height = 'auto'
            body.style.overflow = 'auto'
        }
        if (mainContent) {
            mainContent.style.height = 'auto'
            mainContent.style.overflow = 'initial'
        }

        return () => {
            if (body) {
                body.style.height = '100vh'
                body.style.overflow = 'hidden'
            }
            if (mainContent) {
                mainContent.style.height = '100vh'
                mainContent.style.overflow = 'auto'
            }
            dispatch(resetView());
        };

    }, []);

    useEffect(() => {
        if(aiWidgetTransactionDetails && aiWidgetTransactionDetails?.status === "loading"){
            const entity = aiWidgetTransactionDetails?.entity;
            if(entity){
                generateTransactionForIndividualWidget(entity)
            }
        }
    },[aiWidgetTransactionDetails])


    useEffect(() => {

        if (elementOnFocus && VIEW_DRAWERS.includes(elementOnFocus?.type)) {
            setDrawerElement(adjustFocusElement(elementOnFocus))
            setDrawerOpen(true)
        } else {
            setDrawerOpen(false)
        }
    }, [elementOnFocus])

    useEffect(() => {

        return () => {
            dispatch(setFocusElement({}))
            setDrawerOpen(false)
        }
    }, [pathname])


    useEffect(() => {
        checkForDefaultViewAlert();

        if (selectedView) {
            setViewSchema(selectedView?.view_schema);
            setVaildationWarnings(validateSingleView(selectedView));

            const activeTab = localStorage.getItem("VIEW_TAB_ONFOCUS");
            if (activeTab && activeTab === "datasource") {
                localStorage.removeItem("VIEW_TAB_ONFOCUS");
                setOpenTab(1);
            }
        }
    }, [selectedView])

    const renameCallback = (name) => {
        selectedView.name = name;
        dispatch(setView(selectedView));
    }

    const generateTransactionForIndividualWidget = async (entity) => {
        await generateTransactionWithAI(entity.id);
        dispatch(generateWidgetTransactionWithAI({status: 'idle'}))
        dispatch(setReRender())
    }

    const getFormattedWidgetType = (type) => {
        try {

            if (!type) return ""; // handle empty string

            let allWidgetItems = []
            widgetGroups?.forEach(group => {
                group.widgets?.forEach(item => allWidgetItems.push(item))
            })
            const typeLabel = allWidgetItems?.find(item => item?.value === type)?.title || null;

            if (!typeLabel) return ""; // handle empty string

            return ` | ${typeLabel}`;


        } catch (error) {
            console.error(`Formatting widget type label failed. ${error}`);
            return "";
        }
    }

    const trainViewsLLM = async () => {
        setSendTrainData(true)
        const response = await dispatch(invokeGetService('trainView', []))
        if (!response.status) {
            dispatch(handleSnackBar({ open: true, message: response.message, severity: 'error' }))
        } else {
            dispatch(handleSnackBar({ open: true, message: "Data submitted successfully", severity: 'success' }))
        }
        setSendTrainData(false)
    }

    //     if (!selectedView?.view_schema?.length) return { status: false };

    //     try {
    //         setIsAIGeneratingTransaction(true);
    //         const sequence_id = generateMongoId();
    //         const { name: viewName, desc: viewDesc = "" } = selectedView;
    //         const view_type = (viewName.includes("list") || viewName.includes("overview")) ? "summary" : "details";

    //         selectedView.view_schema.forEach(item => {
    //             addProgressItem({
    //                 id: item.id,
    //                 name: item.name,
    //                 status: "pending",
    //             });
    //         });


    //         const newTransactions = [];
    //         const newFields = [];
    //         const newMessages = [];
    //         const newEvents = [];
    //         const newMappedTransactions = [];
    //         const newHandlers = [];
    //         const processedView = JSON.parse(JSON.stringify(selectedView))

    //         for (let section of processedView.view_schema) {
    //             // if(index > 0) return;
    //             for(let widget of section.children){
    //                 // if(index > 0) return;
    //                 // index++;
    //                 updateProgressItem(section.id, { status: "generating" });

    //                 const data = {
    //                     section_name: section.name,
    //                     section_desc: section.desc || "",
    //                     section_helperText: section.helperText || "",
    //                     id: widget.id,
    //                     view_name: viewName,
    //                     view_desc: viewDesc,
    //                     widgetDetails: widget
    //                 };

    //                 let widgetResponse;
    //                 try {
    //                     setNoficationMessage([
    //                         "Analyzing section structure",
    //                         "Mapping view to digital twin / clusterlet",
    //                         "Selecting database delegate",
    //                         "Designing transaction flow",
    //                         "Validating event sequence",
    //                         "Generating transaction schema"
    //                     ]);
    //                     widgetResponse = await dispatch(invokePostService("generateTransactionForSection", { widgetDetails: data, sequence_id }, {}));
    //                 } catch (err) {
    //                     console.error("Section init failed:", err);
    //                     setNoficationMessage([
    //                         "Transaction generation failed",
    //                         "Unable to validate data pipeline",
    //                         "Please retry or check section configuration"
    //                     ]);
    //                     updateProgressItem(section.id, { status: "error" });
    //                     continue;
    //                 }

    //                 let iterate = true;
    //                 let iterateCount = 0;
    //                 const ITERATE_THRESHOLD = 1
    //                 const isCreate = true;
    //                 while (iterate && ITERATE_THRESHOLD >= iterateCount) {
    //                     ++iterateCount;

    //                     setNoficationMessage([
    //                         "Inspecting view data dependencies",
    //                         "Identifying data sources",
    //                         "Estimating data volume and access patterns",
    //                         "Designing optimal query strategy",
    //                         "Determining number of required queries",
    //                         "Validating query plan for completeness"
    //                     ])
    //                     const queryPlan = await dispatch(invokePostService('llmQueryPlan', {
    //                         viewId: entityId,
    //                         generatedMappedTransactions: widgetResponse?.generatedMappedTransactions,
    //                         section_name: section.slug,
    //                         sequence_id,
    //                         isCreate: isCreate,
    //                         sectionId: section.id
    //                     }))


    //                     if (!queryPlan.status) {
    //                         updateProgressItem(section.id, { status: "error" });
    //                         continue
    //                     };

    //                     setNoficationMessage([
    //                         "Analyzing transaction flow requirements",
    //                         "Identifying inputs, outputs, and intermediate data",
    //                         "Defining message payload structure",
    //                         "Structuring request and response schemas",
    //                         "Ensuring compatibility with handlers",
    //                         "Reviewing message structure integrity",
    //                         "Finalizing transaction message definition"
    //                     ])

    //                     const messageFields = await dispatch(invokePostService('llmViewMessageDef', {
    //                         view: processedView,
    //                         generatedMappedTransactions: queryPlan?.generatedMappedTransactions,
    //                         view_type: view_type,
    //                         sectionId: section.id,
    //                         widgetId: widget.id,
    //                         sequence_id,
    //                         isCreate: isCreate
    //                     }))

    //                     console.log("messageFields--------", messageFields)

    //                     if (!messageFields.status) {
    //                         updateProgressItem(section.id, { status: "error" });
    //                         continue
    //                     };


    //                     setNoficationMessage([
    //                         "Analyzing transaction execution flow",
    //                         "Identifying required handlers (OnSend / OnReceive)",
    //                         "Defining responsibilities for each handler",
    //                         "Specifying data inputs and outputs per handler",
    //                         "Documenting business logic at a high level",
    //                         "Validating handler responsibilities and boundaries",
    //                         "Finalizing handler definitions"
    //                     ])

    //                     const handlerDefinitions = await dispatch(invokePostService('llmFetchHandlerDef', {
    //                         newTransactions: messageFields?.newTransactions,
    //                         newFields: messageFields?.newFields,
    //                         newMessages: messageFields?.newMessages,
    //                         newEvents: messageFields?.newEvents,
    //                         viewId: selectedView._id,
    //                         sectionId: entityId,
    //                         sequence_id,
    //                         isCreate: isCreate
    //                     }))

    //                     if (!handlerDefinitions.status) {
    //                         updateProgressItem(section.id, { status: "error" });
    //                         continue
    //                     };

    //                     setNoficationMessage([
    //                         "Interpreting handler definitions",
    //                         "Generating handler function structure",
    //                         "Implementing business logic within handlers",
    //                         "Integrating message and instance attributes",
    //                         "Ensuring compliance with handler rules",
    //                         "Reviewing generated handler code",
    //                         "Finalizing handler implementation"
    //                     ])

    //                     const handlersCode = await dispatch(invokePostService('llmFetchHandlerCode', {
    //                         newTransactions: handlerDefinitions?.newTransactions,
    //                         newFields: handlerDefinitions?.newFields,
    //                         newMessages: handlerDefinitions?.newMessages,
    //                         newEvents: handlerDefinitions?.newEvents,
    //                         handlersDef: handlerDefinitions?.handlersDef,
    //                         tables: handlerDefinitions.tables,
    //                         structuredTransaction: handlerDefinitions?.structuredTransaction,
    //                         correspondingSection: handlerDefinitions?.correspondingWidget,
    //                         sequence_id,
    //                         isCreate: isCreate
    //                     }))

    //                     if (!handlersCode.status) {
    //                         updateProgressItem(section.id, { status: "error" });
    //                         continue;
    //                     }

    //                     setNoficationMessage([
    //                         "Initiating handler code validation",
    //                         "Checking syntax correctness",
    //                         "Validating handler execution rules",
    //                         "Ensuring correct use of instance and message data",
    //                         "Verifying absence of forbidden operations",
    //                         "Running consistency and completeness checks",
    //                         "Confirming handler code validity"
    //                     ])

    //                     const handlerValidation = await dispatch(invokePostService('llmHandlerValidation', {
    //                         handlersDef: handlersCode?.handlersCode?.events,
    //                         sequence_id,
    //                         tables: handlerDefinitions.tables,
    //                         isCreate: isCreate
    //                     }))
    //                     if (handlerValidation?.isHandlersValid) {
    //                         iterate = false;
    //                         const mappedTransaction = handlersCode?.newTransactions?.[0];
    //                         const mappingObject = {
    //                             source: "transaction",
    //                             transaction: mappedTransaction?._id?.toString(),
    //                             instance: view_type === "summary" ? "Admin" : "{instance}",
    //                             field: widget.name
    //                         }
    //                         if (mappedTransaction) {
    //                             widget.mapping = mappingObject
    //                         }
    //                         console.log({processedView})

    //                         newTransactions.push(...(handlersCode?.newTransactions || []))
    //                         newFields.push(...(handlersCode?.newFields || []))
    //                         newMessages.push(...(handlersCode?.newMessages || []))
    //                         newEvents.push(...(handlersCode?.newEvents || []))
    //                         newMappedTransactions.push(...(messageFields?.generatedMappedTransactions || []))
    //                         newHandlers.push(...(handlersCode?.handlersCode?.events || []))
    //                     } else {
    //                         updateProgressItem(section.id, { status: "error" });
    //                         continue;
    //                     }

    //                     setTimeout(() => {
    //                         updateProgressItem(section.id, { status: "completed" });
    //                     }, 3000)
    //                 }
    //             }
    //         }

    //         console.log("Newtxs-------------", newTransactions)
            
    //         const saveResult = await dispatch(invokePostService('llmSaveViewAndFetchTxns', {
    //             newTransactions: newTransactions,
    //             newFields: newFields,
    //             newMessages: newMessages,
    //             newEvents: newEvents,
    //             generatedMappedTransactions: newMappedTransactions,
    //             newHandlers: newHandlers || [],
    //             view: processedView
    //         }));
            
    //         if (!saveResult.status) {
    //             return { status: false };
    //         }
    //         dispatch(editView(processedView));
    //         dispatch(setReRender());
    //         setIsAIGeneratingTransaction(false);
    //         return { status: true };

    //     } catch (error) {
    //         setIsAIGeneratingTransaction(false);
    //         updateProgressItemAsError();
    //         console.error("AI Transaction generation failed:", error);
    //         return { status: false }
    //     }
    // };

    const processWidget = async ({ section, widget, processedView, viewName, viewDesc, view_type, sequence_id, entityId, selectedView, parallel_triggering_events = [], individualGeneration }) => {
        try{
            updateProgressItem(widget.id, { status: "generating" });

            const data = {
                section_name: section.name,
                section_desc: section.desc || "",
                section_helperText: section.helperText || "",
                id: widget.id,
                view_name: viewName,
                view_desc: viewDesc,
                widgetDetails: widget
            };

            let widgetResponse;

            try {
                setNoficationMessage([
                    "Analyzing section structure",
                    "Mapping view to digital twin / clusterlet",
                    "Selecting database delegate",
                    "Designing transaction flow",
                    "Validating event sequence",
                    "Generating transaction schema"
                ]);

                const widgetResponseRes = await dispatch(
                    invokePostService("generateTransactionForSection", { widgetDetails: data, sequence_id }, {})
                );

                widgetResponse = widgetResponseRes?.data || {};

                console.log("WidgetResponse--------", widgetResponse)

            } catch (err) {
                setNoficationMessage([
                    "Transaction generation failed",
                    "Unable to validate data pipeline",
                    "Please retry or check section configuration"
                ]);

                updateProgressItem(widget.id, { status: "error" });
                return null;
            }

            let iterate = true;
            let iterateCount = 0;
            const ITERATE_THRESHOLD = 1;
            const isCreate = true;

            const result = {
                newTransactions: [],
                newFields: [],
                newMessages: [],
                newEvents: [],
                newMappedTransactions: [],
                newHandlers: []
            };

            const doesAbort = () => {
                const {blueprintId} = getDetailsFromUrl();

                return !blueprintId
            }

            while (iterate && ITERATE_THRESHOLD >= iterateCount) {
                ++iterateCount;

                if(doesAbort()) return;            
                const queryPlanRes = await dispatch(invokePostService('llmQueryPlan', {
                    view: processedView,
                    generatedMappedTransactions: widgetResponse?.generatedMappedTransactions,
                    section_name: section.slug,
                    sequence_id,
                    isCreate,
                    sectionId: section.id,
                    widgetId: widget.id
                }));

                const queryPlan = queryPlanRes?.data || {}

                const genMapTxn = queryPlan?.generatedMappedTransactions
                if(genMapTxn && genMapTxn?.length){
                    genMapTxn.forEach(txn => {
                        let triggerEvent = txn?.children[0];
                        triggerEvent.slug_name = generateUniqueEventName(triggerEvent, parallel_triggering_events)
                    })
                }
                console.log({genMapTxn, parallel_triggering_events})

                if (!queryPlanRes.status) {
                    updateProgressItem(widget.id, { status: "error" });
                    return null;
                }

                setNoficationMessage([
                    "Analyzing transaction flow requirements",
                    "Identifying inputs, outputs, and intermediate data",
                    "Defining message payload structure",
                    "Structuring request and response schemas",
                    "Ensuring compatibility with handlers",
                    "Reviewing message structure integrity",
                    "Finalizing transaction message definition"
                ])


                if(doesAbort()) return;
                const messageFieldsRes = await dispatch(invokePostService('llmViewMessageDef', {
                    view: processedView,
                    generatedMappedTransactions: queryPlan?.generatedMappedTransactions,
                    view_type,
                    sectionId: section.id,
                    widgetId: widget.id,
                    sequence_id,
                    isCreate
                }));

                const messageFields = messageFieldsRes?.data || {};

                if (!messageFieldsRes.status) {
                    updateProgressItem(widget.id, { status: "error" });
                    return null;
                }

                setNoficationMessage([
                    "Analyzing transaction execution flow",
                    "Identifying required handlers (OnSend / OnReceive)",
                    "Defining responsibilities for each handler",
                    "Specifying data inputs and outputs per handler",
                    "Documenting business logic at a high level",
                    "Validating handler responsibilities and boundaries",
                    "Finalizing handler definitions"
                ])

                let handlerDefinitions, handlersCode, handlerValidationStatus, handlerValidation;
                let validateIterate = 0

                while(validateIterate < 2 && !handlerValidationStatus){
                    validateIterate++;
                    if(doesAbort()) return;
                    const handlerDefinitionsRes = await dispatch(invokePostService('llmFetchHandlerDef', {
                        newTransactions: messageFields?.newTransactions,
                        newFields: messageFields?.newFields,
                        newMessages: messageFields?.newMessages,
                        newEvents: messageFields?.newEvents,
                        viewId: selectedView,
                        sectionId: section.id,
                        widgetId: widget.id, 
                        sequence_id,
                        isCreate,
                        failedHandlers: handlerValidation ?? undefined,
                        tables: queryPlan.tables
                    }));

                    handlerDefinitions = handlerDefinitionsRes?.data || {}

                    if (!handlerDefinitionsRes.status) {
                        updateProgressItem(widget.id, { status: "error" });
                        return null;
                    }

                    setNoficationMessage([
                        "Interpreting handler definitions",
                        "Generating handler function structure",
                        "Implementing business logic within handlers",
                        "Integrating message and instance attributes",
                        "Ensuring compliance with handler rules",
                        "Reviewing generated handler code",
                        "Finalizing handler implementation"
                    ])
                    if(doesAbort()) return;
                    const handlersCodeRes = await dispatch(invokePostService('llmFetchHandlerCode', {
                        newTransactions: handlerDefinitions?.newTransactions,
                        newFields: handlerDefinitions?.newFields,
                        newMessages: handlerDefinitions?.newMessages,
                        newEvents: handlerDefinitions?.newEvents,
                        handlersDef: handlerDefinitions?.handlersDef,
                        tables: queryPlan.tables,
                        structuredTransaction: handlerDefinitions?.structuredTransaction,
                        correspondingWidget: handlerDefinitions?.correspondingWidget,
                        sequence_id,
                        isCreate,
                        failedHandlers: handlerValidation ?? undefined
                    }));

                    handlersCode = handlersCodeRes?.data || {};
                
                    if (!handlersCodeRes.status) {
                        updateProgressItem(widget.id, { status: "error" });
                        return null;
                    }

                    setNoficationMessage([
                        "Initiating handler code validation",
                        "Checking syntax correctness",
                        "Validating handler execution rules",
                        "Ensuring correct use of instance and message data",
                        "Verifying absence of forbidden operations",
                        "Running consistency and completeness checks",
                        "Confirming handler code validity"
                    ])
                    if(doesAbort()) return;
                    const handlerLLMCheckRes = await dispatch(invokePostService('llmHandlerValidation', {
                        handlersDef: handlersCode?.handlersCode?.events,
                        sequence_id,
                        tables: handlerDefinitions.tables,
                        isCreate
                    }));

                    const handlerLLMCheck = handlerLLMCheckRes?.data || {};
                    if(doesAbort()) return;
                    const handlerValidationRes = await dispatch(invokePostService('llmLuaValidate', {
                        handlerData: handlerLLMCheck?.handlerData
                    }))

                    handlerValidation = handlerValidationRes?.data || {};

                    if (!handlerValidation?.isHandlersValid || !handlerLLMCheck?.isHandlersValid) {
                        handlerValidationStatus = false;
                        if(validateIterate > 1){
                            updateProgressItem(widget.id, { status: "error" });
                        }
                        continue;
                    }
                    handlerValidationStatus = true;

                }

                iterate = false;

                const mappedTransaction = handlersCode?.newTransactions?.[0];

                if (mappedTransaction) {
                    if (controlTypes.includes(widget.type)) {
                        if (widget.type !== 'control-button' && widget.type !==  'control-joystick' ) {
                            widget.gettertransaction = mappedTransaction?._id?.toString();
                            widget.getterinstance = view_type === "summary" ? "Admin" : "{instance}";
                        }
                    } else {
                        widget.mapping = {
                            source: "transaction",
                            transaction: mappedTransaction?._id?.toString(),
                            instance: view_type === "summary" ? "Admin" : "{instance}",
                            field: widget.name
                        };
                    }
                }

                result.newTransactions.push(...(handlersCode?.newTransactions || []));
                result.newFields.push(...(handlersCode?.newFields || []));
                result.newMessages.push(...(handlersCode?.newMessages || []));
                result.newEvents.push(...(handlersCode?.newEvents || []));
                result.newMappedTransactions.push(...(messageFields?.generatedMappedTransactions || []));
                result.newHandlers.push(...(handlersCode?.handlersCode?.events || []));

                setTimeout(() => {
                    updateProgressItem(widget.id, { status: "completed" });
                }, 3000);
            }

            return result;
        }catch(error){
            updateProgressItem(widget.id, { status: "error" });
        }
    };

    const createDbResponseFailMessage = async () => {
        try {
          const newMesssage = {
            _id: generateMongoId(),
            name: "msg_db_rsp_fail",
            type: 17,
            fromschema: false,
            blueprint_id: blueprintId
          };
          const fieldMessage = {
            name: "message",
            parent: [{ id: String(newMesssage._id), type: "message" }],  
            type: 12,
            attributes: {
              desc: "Message" ,
              type: "string"
            },
            order: 0,
            blueprint_id: blueprintId
          }
          const fieldStatus = {
            name: "status",
            parent: [{ id: String(newMesssage._id), type: "message" }],  
            type: 12,
            attributes: {
              desc: "Status" ,
              type: "string"
            },
            order: 0,
            blueprint_id: blueprintId
          }
          await dispatch(invokePostService('createMessage', { messageData: newMesssage }, []));
          const payloadMessage = { field: fieldMessage, parentId: fieldMessage.parent[0].id, parentType: "message", eventType: true }
          const payloadStatus = { field: fieldStatus, parentId: fieldStatus.parent[0].id, parentType: "message", eventType: true }
    
          const result = await Promise.all([
            dispatch(invokePostService('createField', payloadMessage)),
            dispatch(invokePostService('createField', payloadStatus))
          ]);
         return result;
        } catch (error) {
          console.error(error)
        }
      }
    

    const generateTransactionWithAI = async (widgetId) => {
        if (!selectedView?.view_schema?.length) return { status: false };
    
        try {
            setIsAIGeneratingTransaction(true);
            dispatch(setAiRequestEntity("transaction"))
            dispatch(updateAiAssistant({ loading: true }));
            
            // To add db response failure message if it is not exist to avoid duplicate creation while generating fetch transactions
            const dbResponseFailMessage = messageList.find(item => item.name === "msg_db_rsp_fail");
            if (!dbResponseFailMessage) {
              await createDbResponseFailMessage();
            }
      
    
            const sequence_id = generateMongoId();
            const { name: viewName, desc: viewDesc = "" } = selectedView;
            const view_type = (viewName.includes("list") || viewName.includes("overview")) ? "summary" : "details";
    
            // selectedView.view_schema.forEach(item => {
            //     addProgressItem({ id: item.id, name: item.name, status: "pending" });
            // });
            selectedView.view_schema.forEach(section => {
                section.children?.forEach(widget => {
                    if(widgetId && widgetId === widget.id){
                        addProgressItem({
                            id: widget.id,                 // widget-level tracking
                            name: `${section.desc} → ${widget.desc}`, // better UI label
                            status: "pending"
                        });
                    }else if(!widgetId){
                        addProgressItem({
                            id: widget.id,                 // widget-level tracking
                            name: `${section.desc} → ${widget.desc}`, // better UI label
                            status: "pending"
                        });
                    }
                    
                });
            });
    
            const existingView = JSON.parse(JSON.stringify(selectedView));
            const processedView = JSON.parse(JSON.stringify(selectedView));
    
            /**  PARALLEL EXECUTION PER WIDGET */
            const widgetPromises = [];
            const parallel_triggering_events = [];

            let individualGeneration = false;
            for (const section of processedView.view_schema) {
                for (const widget of section.children) {
                    if(widgetId && widgetId !== widget.id) continue;
                    if(widgetId === widget.id) individualGeneration = true
                    if (widget.type === 'control-button' || widget.type ===   'control-joystick') continue;
                    widgetPromises.push(
                        processWidget({ section, widget, processedView, viewName, viewDesc, view_type, sequence_id, entityId, selectedView, parallel_triggering_events})
                    );
                }
            }
    
            const results = await Promise.all(widgetPromises);

            /** 🔥 Aggregate successful results only */
            const newTransactions = [];
            const newFields = [];
            const newMessages = [];
            const newEvents = [];
            const newMappedTransactions = [];
            const newHandlers = [];
    
            results.forEach(res => {
                if (!res) return;
                newTransactions.push(...res.newTransactions);
                newFields.push(...res.newFields);
                newMessages.push(...res.newMessages);
                newEvents.push(...res.newEvents);
                newMappedTransactions.push(...res.newMappedTransactions);
                newHandlers.push(...res.newHandlers);
            });


            const usedTransactionNames = new Set();
          
            const getUniqueTransactionName = (name) => {
                const baseName = name;
                let uniqueName = baseName;
                let counter = 2;
          
                while (usedTransactionNames.has(uniqueName)) {
                  uniqueName = `${baseName}_${counter}`;
                  counter++;
                }
          
                usedTransactionNames.add(uniqueName);
          
                return uniqueName;
            };

            newTransactions.forEach(transaction => {
                transaction.name = getUniqueTransactionName(transaction.name);
            });

            console.log("Newtxs----------", newTransactions)

            const {blueprintId} = getDetailsFromUrl()

            if(!blueprintId) return {};
            /**  SINGLE FINAL SAVE */
            const saveResult = await dispatch(invokePostService('llmSaveViewAndFetchTxns', {
                newTransactions,
                newFields,
                newMessages,
                newEvents,
                generatedMappedTransactions: newMappedTransactions,
                newHandlers,
                view: processedView,
                existingView,
                individualGeneration
            }));
    
            if (!saveResult.status) return { status: false };
    
            dispatch(editView(processedView));
            dispatch(setReRender());
    
            setIsAIGeneratingTransaction(false);
            dispatch(setAiRequestEntity(null))
            dispatch(updateAiAssistant({ loading: false }));
            return { status: true };
    
        } catch (error) {
            setIsAIGeneratingTransaction(false);
            updateProgressItemAsError();
            dispatch(setAiRequestEntity(null))
            dispatch(updateAiAssistant({ loading: false }));
            console.error("AI Transaction generation failed:", error);
            return { status: false };
        }
    };
    

    const addProgressItem = (item) => {
        setAiProgress((prev) => [...prev, item]);
    };

    const updateProgressItemAsError = () => {
        setAiProgress((prev) => {
            return prev.map((p) => {
                const updated = { ...p, status: "error" };
                return updated;
            });
        });
    };


    const updateProgressItem = (id, changes) => {
        setAiProgress((prev) => {
            return prev.map((p) => {
                if (p.id !== id) return p;
                const updated = { ...p, ...changes };
                return updated;
            });
        });
    };

    const handleRedirectToAppview = async () => {
      const redirectUrlDetails = await dispatch(invokeGetService('redirectLink', {}, true))
      let link;
      if(redirectUrlDetails.status){
        link = redirectUrlDetails.url;
        link += `/view_${selectedView?.name}`
      }

      if(link){
        window.open(link)
      }else{
        dispatch(handleSnackBar({open: true, severity: "error", message: redirectUrlDetails?.message || "Something went wrong"}))
      }
    }

    return (
        <Grid id={`view-${selectedView?.name || ""}-root`} className={`view-root-div ${classes.root}`}>
            {/* <LearnLLMButton  
                onClick={trainViewsLLM} 
                disabled={sendTrainData}
            /> */}
            <BottomDrawer
                isOpen={isDrawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={(drawerElement?.type === 'view' ? "View" : drawerElement?.type === "section" ? "Section" : `Widget`) + (getFormattedWidgetType(elementOnFocus?.widgetType)) + ` | ${drawerElement?.name}`}
                leftPane={<ViewTree data={viewSchema || []} />}
                showTour={drawerElement?.type}
                screen="view"
            >
                {
                    drawerElement?.type === "view" ?
                        <View element={drawerElement} key={drawerElement?.name} />
                        : drawerElement?.type === "section" ?
                            <ViewSection element={drawerElement} key={drawerElement?.name} />
                            : drawerElement?.type === "ui" ?
                                <ViewUI element={drawerElement} key={drawerElement?.name} />
                                : null
                }
            </BottomDrawer>
            <Grid id={"dashboard_builder_tabs"} item xs={12}>
                <Grid container alignItems="center" style={{ position: 'relative' }}>
                    {selectedView && <Grid item style={{ position: 'absolute', left: 0 }}>
                        <Grid container alignItems="center">
                            <div className={classes.avatar}>
                                <img
                                    id="view_icon"
                                    className={`${classes.iconImage} icon_holder`}
                                    src={
                                        typeof selectedIcon === 'string'
                                            ? selectedIcon
                                            : selectedIcon?.link || selectedIcon?.filePath
                                    }
                                    alt="icon"
                                />
                            </div>
                            <EditableTitle id={"view_name"} title={selectedView?.name || ""} entityId={selectedView?._id} titleStyle={{marginLeft: 0, padding: "4px 2px", maxWidth: "250px"}} endPoint={"updateView"} callBack={renameCallback}/>
                            <OwnershipStatusLabel entity={selectedView} />
                            <Tooltip title="Open App view">
                              <Button
                                variant="outlined"
                                size="small"
                                className={classes.openAppBtn}
                                classes={{ startIcon: classes.startIcon }}
                                startIcon={<OpenInNewIcon className={classes.openAppIcon} />}
                                onClick={handleRedirectToAppview}
                              >
                                Open App view
                              </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    }
                    <Grid container style={{ justifyContent: "center" }} alignItems="center">
                        <Grid item>
                            <Button
                                className={openTab === 0 ? classes.activeButton : classes.button}
                                variant="contained"
                                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                onClick={() => onSwitchTab(0)}
                                id="layout_tab"
                                data-ui-tour-id="btn-layout"
                            >
                                <Typography variant="button">Layout</Typography>
                            </Button>
                        </Grid>
                        <Grid item style={{ position: "relative" }}>
                            <Button
                                className={openTab === 1 ? classes.activeButton : classes.button}
                                variant="contained"
                                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                onClick={() => onSwitchTab(1)}
                                id="data_source_tab"
                                data-ui-tour-id="btn-datasource"
                            >
                                <Typography variant="button">Data</Typography>
                            </Button>

                            {viewValidationWarnings?.length > 0 && (
                                <Tooltip title={"Data source mapping is missing in the view"}  >
                                    <ErrorIcon
                                        data-ui-tour-id="datasource-validation-warning"
                                        id="datasource_validation_warning"
                                        style={{
                                            position: "absolute",
                                            top: 2,
                                            right: 2,
                                            fontSize: 17,
                                            color: "rgb(245 127 43)", // MUI yellow (warning)
                                            background: "#fff",
                                            borderRadius: "50%"
                                        }}
                                    />
                                </Tooltip>
                            )}

                        </Grid>
                        <Grid item>
                            <Button
                                className={openTab === 2 ? classes.activeButton : classes.button}
                                variant="contained"
                                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                onClick={() => onSwitchTab(2)}
                                id="simulate_tab"
                                data-ui-tour-id="btn-simulate"
                            >
                                <Typography variant="button">Simulate</Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

            </Grid>

            {showDefaultViewAlert &&
                <Grid item xs={12} className={`defualt-view-alert ${classes.alertContainer}`} >
                    <Alert className={classes.defaultViewAlert} severity="info">
                        <div className={`alert-text`}>You don’t have a default view yet. Choose one to make it your app’s landing page.</div>
                        <Button id={"default-view-alert-button"} className={classes.defaultViewAlertButton} variant="contained" color="primary" onClick={disableDefaultViewAlert} > Okay </Button>
                    </Alert>
                </Grid>
            }
            <Grid id={"dashboard_builder_content"} item xs={12} className={classes.builderContainer}>
                {openTab === 0 &&
                    <SchemaBuilder generateTransactionWithAI={generateTransactionWithAI} isAIGeneratingTransaction={isAIGeneratingTransaction} setMessageList={setMessageList}/>
                }
                {openTab === 1 &&
                    <DataSourceMapping generateTransactionWithAI={generateTransactionWithAI} isAIGeneratingTransaction={isAIGeneratingTransaction} />
                }
                {openTab === 2 &&
                    <DataBuilder />
                }
            </Grid>
            {aiProgress.length ? <AiGenerationNotifications messageList={notificationMessages} items={aiProgress} generationMode = {"parallel"} onClose={() => setAiProgress([])} /> : null}

        </Grid>
    );
};

export default DashboardBuilder;